import React, { useEffect, useState, useRef } from "react";
import classes from './AssetCategory.module.css';
import HeaderNav from '../../Components/Dahboard Header/HeaderNav/HeaderNav';
import Footer from "../../Components/Footer/Footer";
import { useLocation, useNavigate } from 'react-router-dom';
import { Eye, Pencil, Trash2, X, FileText } from 'lucide-react';
import searchicon from "../../Assets/searchicon.png";
import filter from "../../Assets/filter.png";
import axios from "axios";
import { BASE_URL } from "../../Components/API/Api";
import Swal from 'sweetalert2';

const AssetCategory = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isActive, setIsActive] = useState(true);
    const [selectedMetrics, setSelectedMetrics] = useState({
        total_categories: 0,
        total_assets: 0,
    });

    const [loading, setLoading] = useState(true);
    const [modalLoading, setModalLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [assetCategories, setAssetCategories] = useState([]);
    const [companyId, setCompanyId] = useState(null);

    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showViewEditModal, setShowViewEditModal] = useState(false);
    const [modalMode, setModalMode] = useState("view");
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Create Modal state
    const [depreciationMethods, setDepreciationMethods] = useState([]);
    const [assetAccounts, setAssetAccounts] = useState([]);
    const [expenseAccounts, setExpenseAccounts] = useState([]); // Used for both accumulated and expense accounts
    const [createFormData, setCreateFormData] = useState({
        name: "",
        useful_life_years: "",
        depreciation_rate: "",
        depreciation_method_id: "",
        asset_account_id: "",
        accumulated_depreciation_account: "",
        depreciation_expense_account: "",
        company_id: ""
    });
    const [createLoading, setCreateLoading] = useState({
        methods: false,
        assetAccounts: false,
        expenseAccounts: false
    });
    const [submitting, setSubmitting] = useState(false);

    // For searchable selects in create modal
    const [searchTerms, setSearchTerms] = useState({
        depreciation_method: "",
        asset_account: "",
        accumulated_account: "",
        expense_account: ""
    });
    const [showDropdowns, setShowDropdowns] = useState({
        depreciation_method: false,
        asset_account: false,
        accumulated_account: false,
        expense_account: false
    });
    const [dropdownTrigger, setDropdownTrigger] = useState({
        depreciation_method: false,
        asset_account: false,
        accumulated_account: false,
        expense_account: false
    });

    // Create refs for each dropdown
    const depreciationMethodRef = useRef(null);
    const assetAccountRef = useRef(null);
    const accumulatedAccountRef = useRef(null);
    const expenseAccountRef = useRef(null);

    // Modal form data for view/edit
    const [modalData, setModalData] = useState({
        id: "",
        name: "",
        useful_life_years: "",
        depreciation_rate: "",
        depreciation_method_id: "",
        depreciation_method_name: "",
        asset_account_id: "",
        asset_account_name: "",
        accumulated_depreciation_account_id: "",
        accumulated_depreciation_account_name: "",
        depreciation_expense_account_id: "",
        depreciation_expense_account_name: "",
        company_id: ""
    });

    // Helper function to get company ID from any source
    const getCompanyIdFromStorage = () => {
        const directId = localStorage.getItem("company_id");
        if (directId) return directId;

        const companyInfo = localStorage.getItem("companyInfo");
        if (companyInfo) {
            try {
                const parsed = JSON.parse(companyInfo);
                const id = parsed?.id || parsed?.company_id || parsed?.companyId;
                if (id) return String(id);
            } catch (e) {
                console.error("Error parsing companyInfo:", e);
            }
        }

        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const parsed = JSON.parse(userData);
                
                if (parsed.company_id) return String(parsed.company_id);
                
                if (parsed.company) {
                    const id = parsed.company?.id || parsed.company?.company_id || parsed.company?.companyId;
                    if (id) return String(id);
                }
                
                if (parsed.companies?.length > 0) {
                    const mainCompany = parsed.companies.find(c => c.is_main === "1" || c.is_main === 1 || c.is_main === true);
                    if (mainCompany) {
                        const id = mainCompany?.id || mainCompany?.company_id || mainCompany?.companyId;
                        if (id) return String(id);
                    }
                    
                    const firstCompany = parsed.companies[0];
                    const id = firstCompany?.id || firstCompany?.company_id || firstCompany?.companyId;
                    if (id) return String(id);
                }
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }

        return null;
    };

    // Function to get valid company ID
    const getValidCompanyId = () => {
        if (companyId) return companyId;
        
        const storageId = getCompanyIdFromStorage();
        if (storageId) {
            setCompanyId(storageId);
            setCreateFormData(prev => ({
                ...prev,
                company_id: storageId
            }));
            return storageId;
        }
        
        return null;
    };

    // Initialize company ID on mount
    useEffect(() => {
        const id = getCompanyIdFromStorage();
        if (id) {
            console.log("Found company ID:", id);
            setCompanyId(id);
            setCreateFormData(prev => ({
                ...prev,
                company_id: id
            }));
        } else {
            console.warn("No company ID found");
            Swal.fire({
                icon: 'warning',
                title: 'Company ID Missing',
                text: 'Please log in again or select a company.',
                confirmButtonText: 'Go to Login'
            }).then(() => {
                navigate('/login');
            });
        }
    }, [navigate]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (depreciationMethodRef.current && !depreciationMethodRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, depreciation_method: false }));
                setDropdownTrigger(prev => ({ ...prev, depreciation_method: false }));
            }
            if (assetAccountRef.current && !assetAccountRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, asset_account: false }));
                setDropdownTrigger(prev => ({ ...prev, asset_account: false }));
            }
            if (accumulatedAccountRef.current && !accumulatedAccountRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, accumulated_account: false }));
                setDropdownTrigger(prev => ({ ...prev, accumulated_account: false }));
            }
            if (expenseAccountRef.current && !expenseAccountRef.current.contains(event.target)) {
                setShowDropdowns(prev => ({ ...prev, expense_account: false }));
                setDropdownTrigger(prev => ({ ...prev, expense_account: false }));
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch data for create modal when opened
    useEffect(() => {
        if (showCreateModal) {
            fetchDepreciationMethods();
            fetchAssetAccounts();
            fetchExpenseAccounts(); // Fetches both accumulated and expense accounts from same API
        }
    }, [showCreateModal]);

    // Fetch asset categories when companyId is available
    useEffect(() => {
        if (companyId) {
            fetchAssetCategories();
        }
    }, [companyId]);

    const fetchAssetCategories = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");

            if (!token) {
                Swal.fire({
                    icon: 'error',
                    title: 'Authentication Error',
                    text: 'Please log in again'
                });
                setLoading(false);
                return;
            }

            const validCompanyId = getValidCompanyId();

            if (!validCompanyId) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Company ID not found. Please log in again.'
                });
                setLoading(false);
                return;
            }

            const response = await axios.get(`${BASE_URL}/v1/assetcategories`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                params: {
                    company_id: validCompanyId
                }
            });

            if (response.data.status && Array.isArray(response.data.data)) {
                setAssetCategories(response.data.data);

                setSelectedMetrics({
                    total_categories: response.data.data.length,
                    total_assets: response.data.data.reduce((sum, cat) => sum + (cat.assets_count || 0), 0)
                });
            } else {
                setAssetCategories([]);
            }
        } catch (error) {
            console.error("Error fetching asset categories:", error);

            let errorMessage = "Failed to fetch asset categories";
            if (error.response?.data?.message) {
                errorMessage = typeof error.response.data.message === 'object'
                    ? Object.values(error.response.data.message).flat().join(', ')
                    : error.response.data.message;
            } else if (error.response?.status === 401) {
                errorMessage = "Unauthorized. Please log in again.";
            }

            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage
            });

            setAssetCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchDepreciationMethods = async () => {
        setCreateLoading(prev => ({ ...prev, methods: true }));
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`${BASE_URL}/v1/fixedassets/get-depreciation-method`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (Array.isArray(response.data?.data)) {
                setDepreciationMethods(response.data.data);
            } else if (Array.isArray(response.data)) {
                setDepreciationMethods(response.data);
            }
        } catch (error) {
            console.error("Error fetching depreciation methods:", error);
        } finally {
            setCreateLoading(prev => ({ ...prev, methods: false }));
        }
    };

    const fetchAssetAccounts = async () => {
        setCreateLoading(prev => ({ ...prev, assetAccounts: true }));
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = getValidCompanyId();
            
            const response = await axios.get(`${BASE_URL}/v1/fixed-asset-accounts`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });
            
            if (Array.isArray(response.data?.data)) {
                setAssetAccounts(response.data.data);
            } else if (Array.isArray(response.data)) {
                setAssetAccounts(response.data);
            }
        } catch (error) {
            console.error("Error fetching asset accounts:", error);
        } finally {
            setCreateLoading(prev => ({ ...prev, assetAccounts: false }));
        }
    };

    const fetchExpenseAccounts = async () => {
        setCreateLoading(prev => ({ ...prev, expenseAccounts: true }));
        try {
            const token = localStorage.getItem("authToken");
            const validCompanyId = getValidCompanyId();
            
            // Same API for both accumulated and expense accounts
            const response = await axios.get(`${BASE_URL}/v1/get-expense-accounts`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { company_id: validCompanyId }
            });
            
            if (Array.isArray(response.data?.data)) {
                setExpenseAccounts(response.data.data);
            } else if (Array.isArray(response.data)) {
                setExpenseAccounts(response.data);
            }
        } catch (error) {
            console.error("Error fetching expense accounts:", error);
        } finally {
            setCreateLoading(prev => ({ ...prev, expenseAccounts: false }));
        }
    };

    const pageMap = {
        '/dashboard': 'Dashboard',
        '/transactions': 'Transactions',
        '/accounts': 'Accounting',
        '/reports': 'Reports',
        '/banking': 'Banking',
        '/settings': 'Settings',
        '/fixed-assets': 'Fixed Assets',
    };

    const processCategoryData = (category) => {
        const assetAccount = category.asset_account || {};
        const accumulatedAccount = category.accumulated_depre_account || {};
        const expenseAccount = category.depre_expense_account || {};
        const depreciationMethod = category.depre_method || {};

        setModalData({
            id: category.id || "",
            name: category.name || "",
            useful_life_years: category.useful_life_years || "",
            depreciation_rate: category.depreciation_rate || "",
            depreciation_method_id: depreciationMethod.id || category.depreciation_method_id || "",
            depreciation_method_name: depreciationMethod.name || "N/A",
            asset_account_id: assetAccount.id || category.asset_account_id || "",
            asset_account_name: assetAccount.gl_name || "N/A",
            accumulated_depreciation_account_id: accumulatedAccount.id || category.accumulated_depreciation_account_id || "",
            accumulated_depreciation_account_name: accumulatedAccount.gl_name || "N/A",
            depreciation_expense_account_id: expenseAccount.id || category.depreciation_expense_account_id || "",
            depreciation_expense_account_name: expenseAccount.gl_name || "N/A",
            company_id: category.company_id || ""
        });
    };

    const handleViewClick = async (category) => {
        setModalMode("view");
        setSelectedCategory(category);
        setModalLoading(true);
        setShowViewEditModal(true);
        processCategoryData(category);
        setModalLoading(false);
    };

    const handleEditClick = async (category) => {
        setModalMode("edit");
        setSelectedCategory(category);
        setModalLoading(true);
        setShowViewEditModal(true);
        processCategoryData(category);
        setModalLoading(false);
    };

    const handleModalChange = (field, value) => {
        setModalData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCreateChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSearchChange = (field, e) => {
        const value = e.target.value;
        setSearchTerms(prev => ({ ...prev, [field]: value }));
        
        if (!showDropdowns[field]) {
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
        }
        setDropdownTrigger(prev => ({ ...prev, [field]: true }));
    };

    const handleInputClick = (field) => {
        if (!showDropdowns[field]) {
            const currentSearchTerm = searchTerms[field];
            
            if (currentSearchTerm && !dropdownTrigger[field]) {
                setSearchTerms(prev => ({ ...prev, [field]: "" }));
            }
            
            setShowDropdowns(prev => ({ ...prev, [field]: true }));
            setDropdownTrigger(prev => ({ ...prev, [field]: false }));
        } else {
            setDropdownTrigger(prev => ({ ...prev, [field]: false }));
        }
    };

    const handleSelect = (field, item) => {
        const idField = field === 'depreciation_method' ? 'depreciation_method_id' :
            field === 'asset_account' ? 'asset_account_id' :
                field === 'accumulated_account' ? 'accumulated_depreciation_account' :
                    'depreciation_expense_account';

        setCreateFormData(prev => ({
            ...prev,
            [idField]: item.id
        }));

        const displayName = item.gl_name || item.name || "";
        setSearchTerms(prev => ({ ...prev, [field]: displayName }));
        
        setShowDropdowns(prev => ({ ...prev, [field]: false }));
        setDropdownTrigger(prev => ({ ...prev, [field]: false }));
    };

    const getSelectedName = (field) => {
        const idField = field === 'depreciation_method' ? 'depreciation_method_id' :
            field === 'asset_account' ? 'asset_account_id' :
                field === 'accumulated_account' ? 'accumulated_depreciation_account' :
                    'depreciation_expense_account';
        const id = createFormData[idField];

        if (!id) return "";

        let items = [];
        if (field === 'depreciation_method') items = depreciationMethods;
        else if (field === 'asset_account') items = assetAccounts;
        else items = expenseAccounts; // Both accumulated and expense use same data source

        const item = items.find(i => i.id === id);
        return item ? (item.gl_name || item.name || "") : "";
    };

    const filterItems = (field) => {
        const searchTerm = (searchTerms[field] || "").toLowerCase();
        let items = [];

        if (field === 'depreciation_method') items = depreciationMethods;
        else if (field === 'asset_account') items = assetAccounts;
        else items = expenseAccounts; // Both accumulated and expense use same data source

        if (!Array.isArray(items)) {
            return [];
        }

        if (!searchTerm) {
            return items;
        }

        return items.filter(item => {
            if (!item) return false;
            const displayName = (item.gl_name || item.name || "").toLowerCase();
            const code = (item.gl_code || item.code || "").toLowerCase();
            return displayName.includes(searchTerm) || code.includes(searchTerm);
        });
    };

    const handleSave = async () => {
        if (modalMode === "edit") {
            setSaveLoading(true);
            try {
                const token = localStorage.getItem("authToken");
                const validCompanyId = getValidCompanyId();

                if (!validCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Company ID Error',
                        text: 'Company ID not found. Please log in again.'
                    });
                    setSaveLoading(false);
                    return;
                }

                const requestData = {
                    name: modalData.name,
                    useful_life_years: modalData.useful_life_years,
                    depreciation_rate: modalData.depreciation_rate,
                    depreciation_method_id: modalData.depreciation_method_id,
                    asset_account_id: modalData.asset_account_id,
                    accumulated_depreciation_account: modalData.accumulated_depreciation_account_id,
                    depreciation_expense_account: modalData.depreciation_expense_account_id,
                    company_id: validCompanyId
                };

                const response = await axios.post(
                    `${BASE_URL}/v1/assetcategories/update/${modalData.id}`,
                    requestData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        },
                    }
                );

                if (response.data.status) {
                    await fetchAssetCategories();
                    setShowViewEditModal(false);

                    Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: response.data.message || 'Asset category updated successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    let errorMessage = "Failed to update asset category";
                    if (response.data.message) {
                        errorMessage = typeof response.data.message === 'object' 
                            ? Object.values(response.data.message).flat().join(', ')
                            : response.data.message;
                    }

                    Swal.fire({
                        icon: 'error',
                        title: 'Error!',
                        text: errorMessage
                    });
                }
            } catch (error) {
                console.error("Error updating asset category:", error);

                let errorMessage = "Failed to update asset category";
                if (error.response?.data?.message) {
                    errorMessage = typeof error.response.data.message === 'object'
                        ? Object.values(error.response.data.message).flat().join(', ')
                        : error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            } finally {
                setSaveLoading(false);
            }
        } else {
            setShowViewEditModal(false);
        }
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.name ||
            !createFormData.useful_life_years ||
            !createFormData.depreciation_rate ||
            !createFormData.depreciation_method_id ||
            !createFormData.asset_account_id ||
            !createFormData.accumulated_depreciation_account ||
            !createFormData.depreciation_expense_account) {
            Swal.fire("Error", "Please fill in all required fields", "error");
            return;
        }

        const validCompanyId = getValidCompanyId();

        if (!validCompanyId) {
            Swal.fire({
                icon: 'error',
                title: 'Company ID Error',
                text: 'Company ID not found. Please log in again.',
                confirmButtonText: 'Go to Login'
            }).then(() => {
                navigate('/login');
            });
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem("authToken");

            const payload = {
                name: createFormData.name,
                useful_life_years: createFormData.useful_life_years,
                depreciation_rate: createFormData.depreciation_rate,
                depreciation_method_id: createFormData.depreciation_method_id,
                asset_account_id: createFormData.asset_account_id,
                accumulated_depreciation_account: createFormData.accumulated_depreciation_account,
                depreciation_expense_account: createFormData.depreciation_expense_account,
                company_id: validCompanyId
            };

            const response = await axios.post(
                `${BASE_URL}/v1/assetcategories/create`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data?.status) {
                Swal.fire({
                    title: "Success",
                    text: response.data.message || "Asset category created successfully",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false
                });

                onClose();
                await fetchAssetCategories();
            } else {
                let errorMessage = response.data?.message || "Failed to create asset category";
                if (typeof errorMessage === 'object') {
                    errorMessage = Object.values(errorMessage).flat().join(', ');
                }
                Swal.fire("Error", errorMessage, "error");
            }
        } catch (error) {
            console.error("Error creating asset category:", error);

            let errorMessage = "Something went wrong while creating the category";
            if (error.response?.data?.message) {
                if (typeof error.response.data.message === 'object') {
                    errorMessage = Object.values(error.response.data.message).flat().join(', ');
                } else {
                    errorMessage = error.response.data.message;
                }
            }

            Swal.fire("Error", errorMessage, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = async (category) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem("authToken");
                const validCompanyId = getValidCompanyId();
                
                if (!validCompanyId) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Company ID not found. Please try again.'
                    });
                    return;
                }
                
                const response = await axios.delete(
                    `${BASE_URL}/v1/assetcategories/${category.id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        params: {
                            company_id: validCompanyId
                        }
                    }
                );

                if (response.data.status) {
                    await fetchAssetCategories();

                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: response.data.message || 'Asset category deleted successfully!',
                        timer: 3000,
                        showConfirmButton: false
                    });
                } else {
                    throw new Error(response.data.message || "Failed to delete asset category");
                }
            } catch (error) {
                console.error("Error deleting asset category:", error);

                let errorMessage = "Failed to delete asset category";
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: errorMessage,
                    confirmButtonText: 'OK'
                });
            }
        }
    };

    const onClose = () => {
        setShowCreateModal(false);
        const validCompanyId = getValidCompanyId();
        setCreateFormData({
            name: "",
            useful_life_years: "",
            depreciation_rate: "",
            depreciation_method_id: "",
            asset_account_id: "",
            accumulated_depreciation_account: "",
            depreciation_expense_account: "",
            company_id: validCompanyId || ""
        });
        setSearchTerms({
            depreciation_method: "",
            asset_account: "",
            accumulated_account: "",
            expense_account: ""
        });
        setShowDropdowns({
            depreciation_method: false,
            asset_account: false,
            accumulated_account: false,
            expense_account: false
        });
        setDropdownTrigger({
            depreciation_method: false,
            asset_account: false,
            accumulated_account: false,
            expense_account: false
        });
    };

    const TableSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <tr key={index} className={classes.skeletonRow}>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td><div className={classes.skeletonText}></div></td>
                <td>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                        <div className={classes.skeletonIcon}></div>
                    </div>
                </td>
            </tr>
        ));
    };

    const ModalInputSkeleton = () => (
        <div className={classes.skeletonInput}></div>
    );

    return (
        <div>
            <HeaderNav activePage={pageMap[location.pathname] || "Fixed Assets"} />

            <div className={classes.Container}>
                <div className={classes.Container}>
                    <div className={classes.actions}>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assets')}>
                            Assets
                        </button>
                        <button className={`${classes.btns} ${isActive ? classes.active : ""}`} onClick={() => setIsActive(true)}>
                            Categories
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/locations')}>
                            Locations
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/assignments')}>
                            Assignments
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/depreciation')}>
                            Depreciation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/revaluation')}>
                            Revaluation
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/disposal')}>
                            Disposal
                        </button>
                        <button className={classes.btns} onClick={() => navigate('/fixed-assets/maintenance')}>
                            Maintenance
                        </button>
                    </div>

                    <header className={classes.Header}>
                        <h2 className={classes.Title}>Asset Categories</h2>
                    </header>

                    <div className={classes.entrjj}>
                        <div className={classes.allkads}>
                            <div className={classes.thrdkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Categories</p>
                                    <p className={classes.kdptg1}>
                                        {selectedMetrics?.total_categories?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Active categories</p>
                                    </div>
                                </div>
                            </div>
                            <div className={classes.frtkad}>
                                <div className={classes.kad}>
                                    <p className={classes.kdptg}>Total Assets</p>
                                    <p className={classes.kdptg2}>
                                        {selectedMetrics?.total_assets?.toLocaleString() ?? '0'}
                                    </p>
                                    <div className={classes.rvnue}>
                                        <p className={classes.skaltxt}>Across all categories</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={classes.headerActions}>
                        <div className={classes.actionsRight}>
                            <div className={classes.inputWrapper}>
                                <img src={searchicon} alt="Search" className={classes.inputIconGreen} />
                                <input type="text" className={classes.searchBar} placeholder="Search categories" />
                            </div>
                            <div className={classes.inputWrapper}>
                                <input type="text" className={classes.searchBars} placeholder="Filter..." />
                                <img src={filter} alt="Filter" className={classes.inputIcon} />
                            </div>
                        </div>

                        <button className={classes.btnss} onClick={() => setShowCreateModal(true)}>
                            + Create Asset Category
                        </button>
                    </div>

                    <table className={classes.invoiceTable}>
                        <thead>
                            <tr>
                                <th>S/N</th>
                                <th>Category Name</th>
                                <th>Useful Life (Years)</th>
                                <th>Depreciation Rate (%)</th>
                                <th>Depreciation Method</th>
                                <th>Assets Count</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <TableSkeleton />
                            ) : assetCategories.length > 0 ? (
                                assetCategories.map((category, index) => (
                                    <tr key={category.id}>
                                        <td>{index + 1}</td>
                                        <td>{category.name || "—"}</td>
                                        <td>{category.useful_life_years || "—"}</td>
                                        <td>{category.depreciation_rate ? `${category.depreciation_rate}%` : "—"}</td>
                                        <td>{category.depreciation_method?.name || category.depre_method?.name || "—"}</td>
                                        <td>{category.assets_count || 0}</td>
                                        <td>
                                            <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                                <Eye
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#007bff" }}
                                                    onClick={() => handleViewClick(category)}
                                                />
                                                <Pencil
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#28a745" }}
                                                    onClick={() => handleEditClick(category)}
                                                />
                                                <Trash2
                                                    size={16}
                                                    style={{ cursor: "pointer", color: "#dc3545" }}
                                                    onClick={() => handleDeleteClick(category)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: "center", padding: "20px" }}>
                                        {loading ? "Loading..." : "No asset categories found"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View/Edit Modal */}
            {showViewEditModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <FileText className={classes.icon} />
                                <h2>{modalMode === "edit" ? "Edit Asset Category" : "View Asset Category"}</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={() => setShowViewEditModal(false)} disabled={saveLoading}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={classes.formGrid}>
                            {modalLoading ? (
                                Array.from({ length: 7 }).map((_, index) => (
                                    <div className={classes.formGroup} key={index}>
                                        <div className={classes.skeletonLabel}></div>
                                        <ModalInputSkeleton />
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className={classes.formGroup}>
                                        <label>Category Name</label>
                                        <input
                                            type="text"
                                            value={modalData.name}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("name", e.target.value)}
                                            className={modalMode === "edit" ? classes.Field2 : classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Useful Life (Years)</label>
                                        <input
                                            type="number"
                                            value={modalData.useful_life_years}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("useful_life_years", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2}
                                            min="1"
                                            step="1"
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Depreciation Rate (%)</label>
                                        <input
                                            type="number"
                                            value={modalData.depreciation_rate}
                                            readOnly={modalMode === "view" || saveLoading}
                                            onChange={(e) => handleModalChange("depreciation_rate", e.target.value)}
                                            className={modalMode === "edit" ? classes.number2 : classes.Field2}
                                            min="0"
                                            max="100"
                                            step="0.01"
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Depreciation Method</label>
                                        <input
                                            type="text"
                                            value={modalData.depreciation_method_name}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Asset Account</label>
                                        <input
                                            type="text"
                                            value={modalData.asset_account_name}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Accumulated Depreciation Account</label>
                                        <input
                                            type="text"
                                            value={modalData.accumulated_depreciation_account_name}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>

                                    <div className={classes.formGroup}>
                                        <label>Depreciation Expense Account</label>
                                        <input
                                            type="text"
                                            value={modalData.depreciation_expense_account_name}
                                            readOnly
                                            className={classes.Field2}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <button
                            className={classes.saveBtn}
                            onClick={handleSave}
                            disabled={saveLoading}
                        >
                            {saveLoading ? (
                                <span className={classes.loadingText}>
                                    <span className={classes.spinner}></span>
                                    Saving...
                                </span>
                            ) : (
                                modalMode === "edit" ? "Save Changes" : "Close"
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className={classes.overlay}>
                    <div className={classes.modal}>
                        <div className={classes.header}>
                            <div className={classes.title}>
                                <FileText className={classes.icon} />
                                <h2>Create Asset Category</h2>
                            </div>
                            <button className={classes.closeBtn} onClick={onClose}>
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className={classes.formGrid}>
                                <div className={classes.formGroup}>
                                    <label>Category Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="e.g., Machinery, Vehicles, Furniture"
                                        value={createFormData.name}
                                        onChange={handleCreateChange}
                                        required
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Useful Life (Years) *</label>
                                    <input
                                        type="number"
                                        name="useful_life_years"
                                        placeholder="e.g., 5"
                                        value={createFormData.useful_life_years}
                                        onChange={handleCreateChange}
                                        min="1"
                                        step="1"
                                        required
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Depreciation Rate (%) *</label>
                                    <input
                                        type="number"
                                        name="depreciation_rate"
                                        placeholder="e.g., 20"
                                        value={createFormData.depreciation_rate}
                                        onChange={handleCreateChange}
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        required
                                    />
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Depreciation Method *</label>
                                    <div className={classes.searchSelectWrapper} ref={depreciationMethodRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select method..."
                                                value={showDropdowns.depreciation_method ? searchTerms.depreciation_method : getSelectedName('depreciation_method')}
                                                onChange={(e) => handleSearchChange('depreciation_method', e)}
                                                onClick={() => handleInputClick('depreciation_method')}
                                                onFocus={() => handleInputClick('depreciation_method')}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.depreciation_method && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.methods ? (
                                                        <div className={classes.dropdownItem}>Loading methods...</div>
                                                    ) : filterItems('depreciation_method').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No methods found</div>
                                                    ) : (
                                                        filterItems('depreciation_method').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.depreciation_method_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('depreciation_method', item)}
                                                            >
                                                                {item.name}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Asset Account *</label>
                                    <div className={classes.searchSelectWrapper} ref={assetAccountRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select account..."
                                                value={showDropdowns.asset_account ? searchTerms.asset_account : getSelectedName('asset_account')}
                                                onChange={(e) => handleSearchChange('asset_account', e)}
                                                onClick={() => handleInputClick('asset_account')}
                                                onFocus={() => handleInputClick('asset_account')}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.asset_account && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.assetAccounts ? (
                                                        <div className={classes.dropdownItem}>Loading accounts...</div>
                                                    ) : filterItems('asset_account').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No accounts found</div>
                                                    ) : (
                                                        filterItems('asset_account').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.asset_account_id === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('asset_account', item)}
                                                            >
                                                                {item.gl_name} {item.gl_code && `(${item.gl_code})`}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Accumulated Depreciation Account *</label>
                                    <div className={classes.searchSelectWrapper} ref={accumulatedAccountRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select account..."
                                                value={showDropdowns.accumulated_account ? searchTerms.accumulated_account : getSelectedName('accumulated_account')}
                                                onChange={(e) => handleSearchChange('accumulated_account', e)}
                                                onClick={() => handleInputClick('accumulated_account')}
                                                onFocus={() => handleInputClick('accumulated_account')}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.accumulated_account && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.expenseAccounts ? (
                                                        <div className={classes.dropdownItem}>Loading accounts...</div>
                                                    ) : filterItems('accumulated_account').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No accounts found</div>
                                                    ) : (
                                                        filterItems('accumulated_account').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.accumulated_depreciation_account === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('accumulated_account', item)}
                                                            >
                                                                {item.gl_name} {item.gl_code && `(${item.gl_code})`}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={classes.formGroup}>
                                    <label>Depreciation Expense Account *</label>
                                    <div className={classes.searchSelectWrapper} ref={expenseAccountRef}>
                                        <div className={classes.searchInputContainer}>
                                            <input
                                                type="text"
                                                className={classes.searchSelectInput}
                                                placeholder="Search and select account..."
                                                value={showDropdowns.expense_account ? searchTerms.expense_account : getSelectedName('expense_account')}
                                                onChange={(e) => handleSearchChange('expense_account', e)}
                                                onClick={() => handleInputClick('expense_account')}
                                                onFocus={() => handleInputClick('expense_account')}
                                                required
                                            />
                                        </div>

                                        {showDropdowns.expense_account && (
                                            <div className={classes.searchDropdown}>
                                                <div className={classes.dropdownList}>
                                                    {createLoading.expenseAccounts ? (
                                                        <div className={classes.dropdownItem}>Loading accounts...</div>
                                                    ) : filterItems('expense_account').length === 0 ? (
                                                        <div className={classes.dropdownItem}>No accounts found</div>
                                                    ) : (
                                                        filterItems('expense_account').map((item) => (
                                                            <div
                                                                key={item.id}
                                                                className={`${classes.dropdownItem} ${createFormData.depreciation_expense_account === item.id ? classes.selectedItem : ''}`}
                                                                onClick={() => handleSelect('expense_account', item)}
                                                            >
                                                                {item.gl_name} {item.gl_code && `(${item.gl_code})`}
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={classes.saveBtn}
                                disabled={submitting}
                            >
                                {submitting ? "Creating..." : "Create Asset Category"}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default AssetCategory;